// apiService.js

import { formatDateToYYYYMMDD } from '../utilities/dateUtils';
import axios from 'axios';

const normalizeBaseUrl = (baseUrl) => baseUrl.replace(/\/$/, '');

const resolveApiBaseUrl = () => {
  const envBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isHostedDeployment =
      host === 'bankanalysis.aceanalytics.dev' ||
      host.endsWith('.aceanalytics.dev') ||
      host.endsWith('.vercel.app');

    // Use same-origin proxy in hosted environments to avoid cross-origin preflight/CORS issues.
    if (isHostedDeployment) return '/api';
  }

  if (envBaseUrl) return normalizeBaseUrl(envBaseUrl);
  return 'http://localhost:5001/api';
};

const API_BASE_URL = normalizeBaseUrl(resolveApiBaseUrl());
const DEFAULT_FORECAST_ACTOR = 'Jordan Avery';
const FORECAST_ACTOR_STORAGE_KEY = 'rfm-active-user';

const currentForecastActor = () => {
  if (typeof window === 'undefined') return DEFAULT_FORECAST_ACTOR;
  try {
    return window.localStorage.getItem(FORECAST_ACTOR_STORAGE_KEY) || DEFAULT_FORECAST_ACTOR;
  } catch (error) {
    return DEFAULT_FORECAST_ACTOR;
  }
};

const withForecastActor = (params = {}) => ({
  actor: currentForecastActor(),
  ...params
});

export const apiActor = (activeUser) => {
  if (!activeUser) return '';
  if (typeof activeUser === 'string') {
    return activeUser.split(` ${String.fromCharCode(8212)} `)[0].trim();
  }
  return activeUser.name || activeUser.actor || '';
};

// Create an Axios instance for consistent API calls
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Create API methods using Axios
const apiService = {
  fetchLoanBalances: async () => {
    try {
      console.log('Calling API method: fetchLoanBalances');
      const response = await axiosInstance.get('/loan-balances');
      console.log('API Response Data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching loan balances:', error);
      throw error;
    }
  },

  fetchBankParameters: async () => {
    try {
      console.log('Calling API method: fetchBankParameters');
      const response = await axiosInstance.get('/bank-parameters');
      console.log('API Response Data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching bank parameters:', error);
      throw error;
    }
  },

  fetchEconomicAssumptions: async () => {
    try {
      console.log('Calling API method: fetchEconomicAssumptions');
      const response = await axiosInstance.get('/economic-assumptions');
      console.log('API Response Data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching economic assumptions:', error);
      throw error;
    }
  },

  fetchAssetBalances: async () => {
    try {
      console.log('Calling API method: fetchAssetBalances');
      const response = await axiosInstance.get('/asset-balances');
      console.log('Asset balances response status:', response.status);
      console.log('Asset balances data received:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching asset balances:', error);
      throw error;
    }
  },

  saveBankParameters: async (data) => {
    try {
      console.log('Calling API method: saveBankParameters');
      const response = await axiosInstance.post('/bank-parameters', data);
      console.log('API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error saving bank parameters:', error);
      throw error;
    }
  },

  saveEconomicAssumptions: async (data) => {
    try {
      console.log('Calling API method: saveEconomicAssumptions');
      const response = await axiosInstance.post('/economic-assumptions', data);
      console.log('API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error saving economic assumptions:', error);
      throw error;
    }
  },

  calculateRMProForma: async (formData) => {
    try {
      console.log('Sending request to:', `/calculate-rm-pro-forma`);
      console.log('Request data:', JSON.stringify(formData, null, 2));

      // Format dates to YYYY-MM-DD
      if (formData.rmHireDate) {
        formData.rmHireDate = formatDateToYYYYMMDD(formData.rmHireDate);
      }
      if (formData.firstProductionDate) {
        formData.firstProductionDate = formatDateToYYYYMMDD(formData.firstProductionDate);
      }

      const response = await axiosInstance.post('/calculate-rm-pro-forma', formData);
      console.log('API Response:', response.data);
      console.log('API Response type:', typeof response.data);

      // Parse and sanitize response if server returned non-standard JSON (e.g., NaN)
      let responseData = response.data;
      if (typeof responseData === 'string') {
        console.log('Response is string, parsing JSON...');
        try {
          responseData = JSON.parse(responseData);
        } catch (parseErr) {
          console.warn('Initial JSON.parse failed. Attempting to sanitize NaN/Infinity tokens and re-parse.');
          const sanitizedString = responseData
            .replace(/\bNaN\b/g, '0')
            .replace(/\b-Infinity\b/g, '0')
            .replace(/\bInfinity\b/g, '0');
          responseData = JSON.parse(sanitizedString);
        }
      }

      // Deep-sanitize any non-finite numbers that may slip through
      const deepSanitize = (val) => {
        if (Array.isArray(val)) return val.map(deepSanitize);
        if (val && typeof val === 'object') {
          const out = {};
          for (const [k, v] of Object.entries(val)) out[k] = deepSanitize(v);
          return out;
        }
        if (typeof val === 'number' && !Number.isFinite(val)) return 0;
        return val;
      };
      responseData = deepSanitize(responseData);

      // Check for 'annualSummary' key
      if (!responseData.annualSummary) {
        console.error('annualSummary is missing from the API response');
        console.error('Available keys:', Object.keys(responseData));
        throw new Error('annualSummary is missing from the API response');
      }

      return responseData;
    } catch (error) {
      console.error('Error in calculateRMProForma:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      throw error;
    }
  },

  fetchYieldCurves: async (params = {}) => {
    try {
      console.log('Calling API method: fetchYieldCurves (GET)', params);
      const response = await axiosInstance.get('/yield-curves', { params });
      console.log('Yield curves data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching yield curves:', error);
      throw error;
    }
  },

  fetchRegionalForecastBaseline: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/regional-forecast/baseline', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching regional forecast baseline:', error);
      throw error;
    }
  },

  loadRegionalForecast: async (payload) => {
    try {
      const response = await axiosInstance.post('/regional-forecast/load', payload);
      return response.data;
    } catch (error) {
      console.error('Error loading regional forecast:', error);
      throw error;
    }
  },

  // ---- B3: post-load reversal (governed unload) ----
  fetchForecastLoadStatus: async (submissionId) => {
    const response = await axiosInstance.get(
      `/regional-forecast/submissions/${submissionId}/load-status`);
    return response.data;
  },
  reverseForecastLoad: async (submissionId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/unload`, payload);
    return response.data;
  },

  fetchForecastPlanningEntities: async () => {
    const response = await axiosInstance.get('/regional-forecast/planning-entities');
    return response.data;
  },

  fetchForecastCycles: async () => {
    const response = await axiosInstance.get('/regional-forecast/cycles');
    return response.data;
  },

  fetchForecastSubmissions: async (cycleCode = 'FY2027_BUDGET', actingAs) => {
    const params = { cycleCode };
    if (actingAs) params.actingAs = actingAs;
    const response = await axiosInstance.get('/regional-forecast/submissions', { params });
    return response.data;
  },

  fetchForecastRoleContext: async (actor, cycleCode = 'FY2027_BUDGET') => {
    const response = await axiosInstance.get('/regional-forecast/roles/me', {
      params: { actor, cycleCode }
    });
    return response.data;
  },

  // ---- B1: notifications & queues ----
  fetchForecastNotifications: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/notifications', { params });
    return response.data;
  },
  updateForecastNotification: async (notificationId, payload) => {
    const response = await axiosInstance.patch(
      `/regional-forecast/notifications/${notificationId}`, payload);
    return response.data;
  },
  fetchForecastWaitingQueue: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/queues/waiting-on-me', { params });
    return response.data;
  },

  // ---- B2: bulk review actions & saved filter views ----
  bulkTransitionForecastSubmissions: async (payload) => {
    const response = await axiosInstance.post(
      '/regional-forecast/submissions/bulk-transition', payload);
    return response.data;
  },
  fetchForecastFilterViews: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/filter-views', { params });
    return response.data;
  },
  createForecastFilterView: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/filter-views', payload);
    return response.data;
  },
  updateForecastFilterView: async (viewId, payload) => {
    const response = await axiosInstance.put(`/regional-forecast/filter-views/${viewId}`, payload);
    return response.data;
  },
  deleteForecastFilterView: async (viewId, payload) => {
    const response = await axiosInstance.delete(`/regional-forecast/filter-views/${viewId}`, { data: payload });
    return response.data;
  },

  // ---- B4: comment threads & mentions ----
  fetchForecastComments: async (submissionId, params = {}) => {
    const response = await axiosInstance.get(
      `/regional-forecast/submissions/${submissionId}/comments`, { params });
    return response.data;
  },
  createForecastComment: async (submissionId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/comments`, payload);
    return response.data;
  },
  replyForecastComment: async (threadId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/comments/${threadId}/replies`, payload);
    return response.data;
  },
  resolveForecastCommentThread: async (threadId, payload) => {
    const response = await axiosInstance.patch(
      `/regional-forecast/comments/${threadId}`, payload);
    return response.data;
  },

  // ---- B6: paginated audit trail ----
  fetchForecastSubmissionEvents: async (submissionId, params = {}) => {
    const response = await axiosInstance.get(
      `/regional-forecast/submissions/${submissionId}/events`, { params: withForecastActor(params) });
    return response.data;
  },
  fetchForecastSubmissionEventDiff: async (submissionId, eventId, params = {}) => {
    const response = await axiosInstance.get(
      `/regional-forecast/submissions/${submissionId}/events/${eventId}/diff`, { params: withForecastActor(params) });
    return response.data;
  },
  forecastSubmissionEventsCsvUrl: (submissionId, params = {}) => {
    const qs = new URLSearchParams(withForecastActor(params)).toString();
    return `${axiosInstance.defaults.baseURL}/regional-forecast/submissions/${submissionId}/events.csv${qs ? `?${qs}` : ''}`;
  },

  // ---- B7: submission-quality gate preview ----
  previewForecastQualityGates: async (submissionId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/quality-gates`, payload);
    return response.data;
  },

  fetchForecastSubmission: async (submissionId, actingAs) => {
    const response = await axiosInstance.get(`/regional-forecast/submissions/${submissionId}`, {
      params: actingAs ? { actingAs } : undefined
    });
    return response.data;
  },

  saveForecastSubmission: async (submissionId, payload) => {
    const response = await axiosInstance.put(`/regional-forecast/submissions/${submissionId}`, payload);
    return response.data;
  },

  transitionForecastSubmission: async (submissionId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/transition`,
      payload
    );
    return response.data;
  },

  fetchForecastComparison: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/comparison', { params });
    return response.data;
  },

  fetchForecastInsights: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/insights', payload);
    return response.data;
  },

  fetchAutoForecast: async (payload) => {
    // Sonnet thinking + mandatory review + possible revision round: allow 3 minutes.
    const response = await axiosInstance.post('/regional-forecast/auto-forecast', payload, { timeout: 180000 });
    return response.data;
  },

  // ---- Item C: anomaly-adjusted core history + transaction drill-through ----
  fetchCoreHistory: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/core-history', { params });
    return response.data;
  },
  fetchTransactions: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/transactions', { params });
    return response.data;
  },

  // ---- Business-context notes (Item B): durable, tagged AI memory ----
  fetchContextNotes: async (params = {}) => {
    const response = await axiosInstance.get('/regional-forecast/context-notes', { params });
    return response.data;
  },
  createContextNote: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/context-notes', payload);
    return response.data;
  },
  updateContextNote: async (noteGroupId, payload) => {
    const response = await axiosInstance.put(`/regional-forecast/context-notes/${noteGroupId}`, payload);
    return response.data;
  },
  archiveContextNote: async (noteGroupId, payload = {}) => {
    const response = await axiosInstance.delete(`/regional-forecast/context-notes/${noteGroupId}`, { data: payload });
    return response.data;
  },
  fetchContextNoteVersions: async (noteGroupId) => {
    const response = await axiosInstance.get(`/regional-forecast/context-notes/${noteGroupId}`);
    return response.data;
  },

  // ---- AI Prompt Box (Item D): plain-English intent -> reviewable lever changeset ----
  fetchPromptChangeset: async (payload) => {
    // Sonnet 4.6 (extended thinking) primary, OpenRouter fallback: allow 3 minutes.
    const response = await axiosInstance.post('/regional-forecast/prompt-intent', payload, { timeout: 180000 });
    return response.data;
  },
  recordForecastAdjustment: async (submissionId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/adjust-event`,
      payload
    );
    return response.data;
  },

  // ---- Corporate Projects ----
  fetchProjects: async () => {
    const response = await axiosInstance.get('/regional-forecast/projects');
    return response.data;
  },
  fetchProjectOpenIds: async () => {
    const response = await axiosInstance.get('/regional-forecast/projects/open-ids');
    return response.data;
  },
  createProject: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/projects', payload);
    return response.data;
  },
  fetchProject: async (projectId) => {
    const response = await axiosInstance.get(`/regional-forecast/projects/${projectId}`);
    return response.data;
  },
  saveProject: async (projectId, payload) => {
    const response = await axiosInstance.put(`/regional-forecast/projects/${projectId}`, payload);
    return response.data;
  },
  transitionProject: async (projectId, payload) => {
    const response = await axiosInstance.post(`/regional-forecast/projects/${projectId}/transition`, payload);
    return response.data;
  },
  ingestProjectDocument: async (projectId, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(
      `/regional-forecast/projects/${projectId}/ingest`, form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    return response.data;
  },
  draftProjectProForma: async (projectId, guidance) => {
    // Planner (150s) + OpenRouter fallback + reviewer + deadline-guarded
    // revision round can run up to the Vercel 300s window — cover it fully.
    const response = await axiosInstance.post(
      `/regional-forecast/projects/${projectId}/draft`, { guidance }, { timeout: 295000 }
    );
    return response.data;
  },
  computeProjectFinancials: async (template) => {
    const response = await axiosInstance.post('/regional-forecast/projects/compute', { template });
    return response.data;
  },
  validateProjectSpread: async (projectId, template) => {
    const response = await axiosInstance.post(
      `/regional-forecast/projects/${projectId}/spread/validate`, { template }
    );
    return response.data;
  },
  exportProjectSpreadWorkbook: async (projectId) => {
    const response = await axiosInstance.get(
      `/regional-forecast/projects/${projectId}/spread/export`,
      { responseType: 'blob' }
    );
    return response;
  },
  previewProjectSpreadWorkbookImport: async (projectId, file, actor) => {
    const form = new FormData();
    form.append('file', file);
    form.append('actor', actor || '');
    const response = await axiosInstance.post(
      `/regional-forecast/projects/${projectId}/spread/import-preview`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    );
    return response.data;
  },
  applyProjectSpreadWorkbookImport: async (projectId, importId, actor) => {
    const response = await axiosInstance.post(
      `/regional-forecast/projects/${projectId}/spread/imports/${importId}/apply`,
      { actor }
    );
    return response.data;
  },
  loadProjectToGl: async (projectId, actor) => {
    const response = await axiosInstance.post(`/regional-forecast/projects/${projectId}/load`, { actor });
    return response.data;
  },
  // D2 proposal review tracker.
  fetchProjectProposals: async (projectId, params = {}) => {
    const response = await axiosInstance.get(
      `/regional-forecast/projects/${projectId}/proposals`, { params }
    );
    return response.data;
  },
  decideProjectProposals: async (projectId, payload) => {
    const response = await axiosInstance.patch(
      `/regional-forecast/projects/${projectId}/proposals`, payload
    );
    return response.data;
  },
  bulkAcceptProjectProposals: async (projectId, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/projects/${projectId}/proposals/bulk-accept`, payload
    );
    return response.data;
  },
  // D3 accounting policy pack (read-only reference).
  fetchProjectPolicyPack: async () => {
    const response = await axiosInstance.get('/regional-forecast/projects/policy-pack');
    return response.data;
  },
  // D3 reviewer override of a per-line policy decision (reason required).
  overrideProjectPolicyDecision: async (projectId, decisionId, payload) => {
    const response = await axiosInstance.patch(
      `/regional-forecast/projects/${projectId}/policy-decisions/${decisionId}`, payload
    );
    return response.data;
  },

  fetchCentralSettings: async (cycleCode = 'FY2027_BUDGET') => {
    const response = await axiosInstance.get('/regional-forecast/central-settings', { params: { cycleCode } });
    return response.data;
  },

  saveCentralSettings: async (payload) => {
    const response = await axiosInstance.put('/regional-forecast/central-settings', payload);
    return response.data;
  },

  fetchMacroScenarios: async (cycleCode = 'FY2027_BUDGET') => {
    const response = await axiosInstance.get('/regional-forecast/macro-scenarios', { params: { cycleCode } });
    return response.data;
  },

  saveMacroScenario: async (scenarioKey, payload) => {
    const response = await axiosInstance.put(
      `/regional-forecast/macro-scenarios/${encodeURIComponent(scenarioKey)}`,
      payload
    );
    return response.data;
  },

  publishMacroScenario: async (scenarioKey, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/macro-scenarios/${encodeURIComponent(scenarioKey)}/publish`,
      payload
    );
    return response.data;
  },

  archiveMacroScenario: async (scenarioKey, payload) => {
    const response = await axiosInstance.post(
      `/regional-forecast/macro-scenarios/${encodeURIComponent(scenarioKey)}/archive`,
      payload
    );
    return response.data;
  },

  compareMacroScenarios: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/macro-scenarios/compare', payload);
    return response.data;
  },

  fetchDriverViewAnchor: async ({ entity, cycleCode = 'FY2027_BUDGET', scenarioKey = 'Base' }) => {
    const response = await axiosInstance.get('/regional-forecast/driver-view-anchor', {
      params: { entity, cycleCode, scenarioKey }
    });
    return response.data;
  },

  rebuildDriverViewAnchors: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/driver-view-anchors/rebuild', payload);
    return response.data;
  },

  fetchHrTransfers: async ({ division, market, cycleCode = 'FY2027_BUDGET' }) => {
    const response = await axiosInstance.get('/regional-forecast/hr-transfers', { params: { division, market, cycleCode } });
    return response.data;
  },

  fetchHrRoster: async (entity) => {
    const response = await axiosInstance.get('/regional-forecast/hr-roster', { params: { entity } });
    return response.data;
  },

  fetchVarianceReview: async (payload) => {
    // Provider chain may try two OpenRouter models back to back.
    const response = await axiosInstance.post('/regional-forecast/insights/variance-review', payload, { timeout: 150000 });
    return response.data;
  },

  fetchExecutiveNarrative: async (payload) => {
    const response = await axiosInstance.post('/regional-forecast/insights/executive-narrative', payload, { timeout: 150000 });
    return response.data;
  },

  generateForecastChangeSummary: async (submissionId) => {
    const response = await axiosInstance.post(`/regional-forecast/submissions/${submissionId}/summary`);
    return response.data;
  },

  exportForecastSubmissionWorkbook: async (submissionId, actor) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/excel/export`,
      { actor },
      { responseType: 'blob' }
    );
    return response;
  },

  previewForecastSubmissionWorkbookImport: async (submissionId, file, actor) => {
    const form = new FormData();
    form.append('file', file);
    form.append('actor', actor || '');
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/excel/import-preview`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    );
    return response.data;
  },

  markForecastSubmissionWorkbookImportApplied: async (submissionId, importId, actor) => {
    const response = await axiosInstance.post(
      `/regional-forecast/submissions/${submissionId}/excel/imports/${importId}/applied`,
      { actor }
    );
    return response.data;
  },
};

export default apiService;
